import PropTypes from 'prop-types';
import { useRef, useEffect, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import normalizeAssertionBaseUrl from 'util/normalizeAssertionBaseUrl';
import {
  buildIfpPalPaymentPopupFeatures,
  IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
} from './popupConstants';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorText: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  statusText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
  popupBlockedText: {
    color: theme.palette.warning.main,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
  },
}));

/** Popup receives JWT via postMessage so the iframe URL stays under F5 query limits. */
const JWT_DELIVERY_POST_MESSAGE = 'postMessage';
const IFP_PAL_JWT_REQUEST = 'ifpPalPayment:requestJwt';
const IFP_PAL_JWT_RESPONSE = 'ifpPalPayment:jwt';
/** How often to pull focus back to the payment popup (ms). */
const PAYMENT_POPUP_FOREGROUND_MS = 250;

function focusPaymentWindow(popup) {
  if (!popup || popup.closed) return;
  try {
    popup.focus();
  } catch (_e) {
    // cross-origin or closed during focus
  }
}

const PaymentIframe = ({
  jwt,
  fspId,
  assertionBaseUrl,
  ifpAssertionVariant,
  iframeHtml,
  keepWindowFocused,
  onError,
  onPopupClosed,
  onPopupReady,
  onJwtDelivered,
  preOpenedPopup,
}) => {
  const classes = useStyles();
  const popupRef = useRef(null);
  const jwtDeliveredRef = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Opening payment window...');
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const popupGuardKey = 'PAYRAILZ_IFP_PAL_PAYMENT_POPUP_GUARD';

  const normalizedAssertionBaseUrl = useMemo(
    () => normalizeAssertionBaseUrl(assertionBaseUrl),
    [assertionBaseUrl]
  );

  useEffect(() => {
    const clearGuard = () => {
      if (keepWindowFocused) {
        globalThis[popupGuardKey] = false;
      }
    };

    if (keepWindowFocused) {
      if (globalThis[popupGuardKey]) {
        return undefined;
      }
      globalThis[popupGuardKey] = true;
    }
    if (!jwt || !fspId || !assertionBaseUrl) {
      const errorMessage = 'Missing required parameters for payment popup';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      clearGuard();
      return undefined;
    }

    const queryParams = new URLSearchParams({
      fspId: String(fspId),
      assertionBaseUrl: normalizedAssertionBaseUrl,
    });
    queryParams.set('ifpVariant', ifpAssertionVariant);
    queryParams.set('jwtDelivery', JWT_DELIVERY_POST_MESSAGE);

    const palProxyBaseUrl = process.env.REACT_APP_PAL_PROXY_BASE_URL;
    const localBffOrigin = process.env.REACT_APP_LOCAL_PAL_IFP_BFF_ORIGIN;
    const localUiOrigin = process.env.REACT_APP_LOCAL_PAL_IFP_UI_ORIGIN;
    if (palProxyBaseUrl) {
      queryParams.set('proxyBaseUrl', palProxyBaseUrl);
    }
    const usesPalIfpBff =
      ifpAssertionVariant === 'enrolled' || ifpAssertionVariant === 'guest-v2';
    if (usesPalIfpBff && localBffOrigin) {
      queryParams.set('bffOrigin', localBffOrigin);
    }
    if (usesPalIfpBff && localUiOrigin) {
      queryParams.set('palIfpUiOrigin', localUiOrigin);
    }

    const popupUrl = `${
      window.location.origin
    }/${iframeHtml}?${queryParams.toString()}`;

    const popupFeatures = buildIfpPalPaymentPopupFeatures();

    let popup;

    const handleJwtRequest = (event) => {
      if (!popup || event.source !== popup) {
        return;
      }
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data && event.data.type === IFP_PAL_JWT_REQUEST) {
        try {
          popup.postMessage(
            { type: IFP_PAL_JWT_RESPONSE, data: { jwt } },
            window.location.origin
          );
          if (onJwtDelivered && !jwtDeliveredRef.current) {
            jwtDeliveredRef.current = true;
            onJwtDelivered();
          }
        } catch (_postMessageError) {
          // popup may have navigated cross-origin
        }
      }
    };
    window.addEventListener('message', handleJwtRequest);

    if (preOpenedPopup && !preOpenedPopup.closed) {
      popup = preOpenedPopup;
    } else if (!keepWindowFocused && preOpenedPopup) {
      window.removeEventListener('message', handleJwtRequest);
      if (onPopupClosed) onPopupClosed();
      return undefined;
    } else {
      popup = window.open(
        'about:blank',
        IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
        popupFeatures
      );

      if (!popup || popup.closed || popup.closed === undefined) {
        window.removeEventListener('message', handleJwtRequest);
        setIsPopupBlocked(true);
        setError(
          'Popup window was blocked. Please allow popups for this site and try again.'
        );
        if (onError) onError('Popup window was blocked');
        clearGuard();
        return undefined;
      }
    }

    popupRef.current = popup;
    if (onPopupReady) {
      onPopupReady(popup);
    }

    try {
      popup.location.href = popupUrl;
    } catch (_navError) {
      window.removeEventListener('message', handleJwtRequest);
      setError('Failed to navigate payment window');
      if (onError) onError('Failed to navigate payment window');
      clearGuard();
      return undefined;
    }

    if (keepWindowFocused) {
      focusPaymentWindow(popup);
    }
    setStatus('Payment window opened');

    const shouldSkipRefocusBecauseDialogFocused = () => {
      const active = document.activeElement;
      if (!active || !active.closest) return false;
      return Boolean(active.closest('[role="dialog"]'));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (shouldSkipRefocusBecauseDialogFocused()) return;
      focusPaymentWindow(popup);
    };

    const handleOpenerBlur = () => {
      if (shouldSkipRefocusBecauseDialogFocused()) return;
      window.setTimeout(() => focusPaymentWindow(popup), 0);
    };

    if (keepWindowFocused) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('blur', handleOpenerBlur);
    }

    const mainInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(mainInterval);
        clearGuard();
        if (onPopupReady) {
          onPopupReady(null);
        }
        if (onPopupClosed) onPopupClosed();
        return;
      }
      if (keepWindowFocused && !shouldSkipRefocusBecauseDialogFocused()) {
        focusPaymentWindow(popup);
      }
    }, PAYMENT_POPUP_FOREGROUND_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleOpenerBlur);
      window.removeEventListener('message', handleJwtRequest);
      clearInterval(mainInterval);
      clearGuard();
      if (onPopupReady) {
        onPopupReady(null);
      }
    };
  }, [
    jwt,
    fspId,
    assertionBaseUrl,
    normalizedAssertionBaseUrl,
    ifpAssertionVariant,
    keepWindowFocused,
    onError,
    onPopupClosed,
    onPopupReady,
    onJwtDelivered,
    preOpenedPopup,
    iframeHtml,
  ]);

  if (error) {
    return (
      <div className={classes.errorContainer}>
        <div className={classes.errorText}>{error}</div>
        {isPopupBlocked && (
          <div className={classes.popupBlockedText}>
            <strong>Popup Blocked</strong>
            <p>Your browser blocked the payment window. Please:</p>
            <ul style={{ textAlign: 'left', display: 'inline-block' }}>
              <li>Allow popups for this site in your browser settings</li>
              <li>Try clicking the payment button again</li>
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.statusText}>{status}</div>
    </div>
  );
};

PaymentIframe.propTypes = {
  jwt: PropTypes.string.isRequired,
  fspId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  assertionBaseUrl: PropTypes.string.isRequired,
  ifpAssertionVariant: PropTypes.oneOf(['guest', 'guest-v2', 'enrolled']),
  iframeHtml: PropTypes.string,
  keepWindowFocused: PropTypes.bool,
  onError: PropTypes.func,
  onPopupClosed: PropTypes.func,
  onPopupReady: PropTypes.func,
  onJwtDelivered: PropTypes.func,
  preOpenedPopup: PropTypes.shape({
    closed: PropTypes.bool,
    close: PropTypes.func,
    location: PropTypes.shape({
      href: PropTypes.string,
    }),
  }),
};

PaymentIframe.defaultProps = {
  ifpAssertionVariant: 'guest',
  iframeHtml: 'ifpPalPaymentIframe.html',
  keepWindowFocused: true,
  onError: null,
  onPopupClosed: null,
  onPopupReady: null,
  onJwtDelivered: null,
  preOpenedPopup: null,
};

export default PaymentIframe;
